import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DataChange } from 'devextreme/common/grids';
import Tabs, { Item } from 'devextreme-react/tabs';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { Toast } from 'devextreme-react/toast';
import { formatMessage } from 'devextreme/localization';

import { Button, TextBox } from 'devextreme-react';

import MessageForm, { MessageFormHandle, ErrorMessage } from '../../components/message-form/MessageForm';
import PathApi from '../../api/PathApi';
import PathVersionStatus from '../../components/path-version-status/PathVersionStatus';
import ChangeInProgressForm from '../../components/change-in-progress-form/ChangeInProgressForm';

import SourceSelection from '../../components/source-selection/SourceSelection';
import FiltersSelection from '../../components/filters-selection/FiltersSelection';
import useFilteringCapabilities from '../../components/filters-selection/useFilteringCapabilities';
import { collectDeclaredParameters } from '../../components/filter-selection/FilterBuilderInline';

import AIExposedFields, { AIExposedFieldsHandle } from '../../components/ai-exposed-fields/AIExposedFields';
import AIResume from '../../components/ai-resume/AIResume';

import PathAIApi from '../../api/PathAIApi';

import GetPathAIDetailResult from '../../classes/api/result/pathaimodule/GetPathAIDetailResult';
import PathAIMappingDto from '../../classes/dtos/pathaimodule/PathAIMappingDto';

import DataTableReadDto from '../../classes/dtos/pathcommonmodule/DataTableReadDto';
import DataSourceDataTableReadDto from '../../classes/dtos/pathcommonmodule/DataSourceDataTableReadDto';
import PathDataTableReadDto from '../../classes/dtos/pathcommonmodule/PathDataTableReadDto';

import AIFieldMappingDto from '../../classes/dtos/pathaimodule/AIFieldMappingDto';

import PathCheckResultDto from '../../classes/dtos/pathmodule/PathCheckResultDto';
import { checkPathBeforeSaveAndPublish, getPathPublishChecklist, PathBeforeSaveValidationInput, ValidationRow } from '../../logics/pathmodule/PathBeforeSaveValidation';
import { performPathSave } from '../../logics/pathmodule/performPathSave';
import { useAuth } from '../../contexts/auth';

import TransformationCapabilitiesDto from '../../classes/dtos/transformationmodule/TransformationCapabilitiesDto';
import TransformationNotConfigurableDto from '../../classes/dtos/transformationmodule/TransformationNotConfigurableDto';
import PathAIMappingCapabilitiesDto from '../../classes/dtos/pathaimodule/PathAIMappingCapabilitiesDto';

import DataTypeDto from '../../classes/dtos/datastructuremodule/DataTypeDto';

import FieldResumeDto from '../../classes/dtos/pathcommonmodule/FieldResumeDto';


export default function PathAI() {

    const { id } = useParams();


    const { sessionDetail } = useAuth();
    const navigate = useNavigate();

    const [searchParams, setSearchParams] = useSearchParams();

    const pathId = Number.parseInt(id ?? "0");

    // Feature flag BD (Feature.FilterVisibility) : première condition d'affichage de l'onglet "Filters".
    const filterTabsEnabled = sessionDetail?.WithFilterVisibility ?? false;

    const messageFormHandleRef = useRef<MessageFormHandle>(null);

    const metaApiExposedFieldsRef = useRef<AIExposedFieldsHandle>(null);

    const [pathAIDetail, setPathAIDetail] = useState<GetPathAIDetailResult | null>(null);

    // "Advanced mode" checkbox (FILTER column header). Page-level state, persisted on the mapping version.
    const [advancedFilterMode, setAdvancedFilterMode] = useState<boolean>(false);
    // Advanced mode is a one-way switch: once it has been saved it can't be turned back off.
    const [advancedFilterModeLocked, setAdvancedFilterModeLocked] = useState<boolean>(false);

    // L'onglet "Filters" (mode complexe) n'est disponible que si la feature BD est active ET que le mode avancé
    // est activé sur ce path — fusion du feature flag Feature.FilterVisibility et de l'ancien `!simpleFilterMode`.
    const filtersTabAvailable = filterTabsEnabled && advancedFilterMode;

    // Colonne FILTER des grilles : visible seulement si la table source gère le filtrage. Même calcul
    // (`withFilter`) que l'onglet "Filters" dans FiltersSelection, via le hook partagé.
    const filterSourceTableReferenceId = pathAIDetail?.Source instanceof DataSourceDataTableReadDto ? (pathAIDetail.Source.TableReferenceId ?? 0) : 0;
    const { withFilter: sourceHandlesFilters } = useFilteringCapabilities(filterSourceTableReferenceId);

    const [capabilities, setCapabilities] = useState<PathAIMappingCapabilitiesDto[]>([]);
    const [transformationsCapabilities, setTransformationsCapabilities] = useState<TransformationCapabilitiesDto[]>([]);

    const [mapping, setMapping] = useState<AIFieldMappingDto[]>([]);

    const MAX_TAB = 3;
    const FILTERS_TAB = 2;

    const [currentTab, setCurrentTab] = useState<number>(() => {
        const tabFromUrl = Number(searchParams.get('tab'));
        const isValid = !Number.isNaN(tabFromUrl) && tabFromUrl >= 0 && tabFromUrl <= MAX_TAB && (filterTabsEnabled || tabFromUrl !== FILTERS_TAB);
        return isValid ? tabFromUrl : 0;
    });

    const [isOpen, setIsOpen] = useState<boolean>(false);

    const changeInProgress = useRef<boolean>(false);

    // Mirror of changeInProgress for rendering (the ref alone doesn't trigger a re-render).
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
    const [isPublished, setIsPublished] = useState<boolean>(false);

    const markChanged = useCallback(() => {
        changeInProgress.current = true;
        setHasUnsavedChanges(true);
    }, []);

    const [editNameInprogress, setEditNameInprogress] = useState<string | null>(null);

    const dataChangesRef = useRef<DataChange[]>([]);

    const selectedIdsRef = useRef<number[]>([]);

    const pathAIMappingDtosRef = useRef<PathAIMappingDto[]>([]);

    const [toastConfig, setToastConfig] = useState({
        isVisible: false,
        type: 'info',
        message: '',
        withRedirect: false
    } as {
        isVisible: boolean,
        type: 'info' | 'error' | 'success',
        message: string,
        withRedirect: boolean
    });

    useEffect(() => {
        PathAIApi.GetPathAIDetailAsync(pathId)
            .then(detail => {
                if (detail.IsFailed()) {
                    navigate("/paths");
                }
                else {
                    setPathAIDetail(detail.Result as GetPathAIDetailResult);
                    setAdvancedFilterMode((detail.Result as GetPathAIDetailResult).AdvancedFilterMode);
                    setIsPublished((detail.Result as GetPathAIDetailResult).PublishedVersionId !== null && (detail.Result as GetPathAIDetailResult).PublishedVersionId === (detail.Result as GetPathAIDetailResult).LastVersionId);
                    setAdvancedFilterModeLocked((detail.Result as GetPathAIDetailResult).AdvancedFilterMode);
                }
            });
    }, [pathId, navigate]);

    const dataLoad = useCallback(async () => {

        if (pathAIDetail === null) {
            return;
        }

        if (pathAIDetail.Source === null) {
            return;
        }

        let tableReferenceId: number | null = null;
        let pathId: number | null = null;

        if (pathAIDetail.Source instanceof DataSourceDataTableReadDto) {
            if ((pathAIDetail.Source.DataSourceId ?? 0) > 0 && (pathAIDetail.Source.TableReferenceId ?? 0) > 0) {
                tableReferenceId = pathAIDetail.Source.TableReferenceId;
            }
        }

        if (pathAIDetail.Source instanceof PathDataTableReadDto) {
            if ((pathAIDetail.Source.PathId ?? 0) > 0) {
                pathId = pathAIDetail.Source.PathId;
            }
        }

        if (tableReferenceId !== null || pathId !== null) {

            let getPathAICapabilitiesResult = await PathAIApi.GetPathAICapabilitiesAsync(tableReferenceId, pathId);

            if (getPathAICapabilitiesResult.IsFailed()) {
                alert("Error : " + getPathAICapabilitiesResult.ErrorMessage);
                return;
            }

            let allFieldsSource = getPathAICapabilitiesResult.Result.SourceFields
                .toSorted((a, b) => (a.Name.toLowerCase() > b.Name.toLowerCase()) ? 1 : (a.Name.toLowerCase() < b.Name.toLowerCase()) ? -1 : 0);

            setCapabilities(getPathAICapabilitiesResult.Result.MappingsCapabilities);
            let transformationsCapabilitiesTemp = getPathAICapabilitiesResult.Result.TransformationsCapabilities;
            transformationsCapabilitiesTemp.push(new TransformationNotConfigurableDto(0, "Direct", 0, new DataTypeDto(0, false, ""), new DataTypeDto(0, false, ""), "TransformationNotConfigurable"));
            setTransformationsCapabilities(transformationsCapabilitiesTemp);

            let Mappings = pathAIMappingDtosRef.current.length > 0
                ? pathAIMappingDtosRef.current.map(e => new PathAIMappingDto(e.FieldReferenceId, e.TransformationLogicId, e.TransformationParameters, e.DisplayName, e.Description, selectedIdsRef.current.includes(e.FieldReferenceId), e.Filters, e.DeclaredVariables))
                : (pathAIDetail?.Mappings ?? []);

            let mappingTemp = allFieldsSource.map((e) => {
                let readMapping = Mappings?.find(x => x.FieldReferenceId === e.Id) ?? null;
                if (readMapping === null) {
                    return new AIFieldMappingDto(e.Id, null, null, e.Name, e.Type, e.Name, '', []);
                } else {
                    return new AIFieldMappingDto(e.Id, readMapping.TransformationLogicId, readMapping.TransformationParameters, e.Name, e.Type, readMapping.DisplayName, readMapping.Description, readMapping.Filters, readMapping.DeclaredVariables);
                }
            })

            selectedIdsRef.current = Mappings.filter(e => e.IsSelected).map(e => e.FieldReferenceId);

            setMapping(mappingTemp);

            let result = new Array<PathAIMappingDto>();

            mappingTemp.forEach((e: AIFieldMappingDto) => {
                let readMapping = Mappings.find(x => x.FieldReferenceId === e.SourceFieldReferenceId) ?? null;
                result.push(new PathAIMappingDto(e.SourceFieldReferenceId ?? 0, e.TransformationType ?? 0, e.TransformationParameters ?? "", e.DisplayName, e.Description, readMapping?.IsSelected ?? false, e.Filters, e.DeclaredVariables));
            });

            pathAIMappingDtosRef.current = result;
        } else {
            setCapabilities([]);
            setTransformationsCapabilities([]);
            setMapping([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathAIDetail?.Source]);

    useEffect(() => {
        dataLoad()
    }, [dataLoad]);

    // The name is saved on its own (not part of the version): it never creates a version nor counts as an unsaved change.
    async function handleNameChange() {
        const newName = editNameInprogress;
        if (newName === null) {
            return;
        }

        const renameResult = await PathApi.RenamePathAsync(pathId, newName);
        if (renameResult.IsFailed()) {
            messageFormHandleRef.current?.Open(formatMessage("PathValidation_SaveFailed"), ErrorMessage);
            return;
        }

        setPathAIDetail((previous) => {
            if (previous === null) {
                return previous;
            }
            previous.Name = newName;
            return previous;
        });
        setEditNameInprogress(null);
        setToastConfig({
            ...toastConfig,
            isVisible: true,
            type: 'success',
            message: formatMessage('PathName_Saved'),
            withRedirect: false
        });
    }

    const handleDataChanges = useCallback((changes: DataChange[], origin: AIFieldMappingDto[]) => {
        dataChangesRef.current = changes;

        // Grid edits are unsaved changes too (the save buttons depend on it).
        if (changes.length > 0) {
            markChanged();
        }

        let result = new Array<PathAIMappingDto>();

        origin.forEach((e: AIFieldMappingDto) => {
            let existing = pathAIMappingDtosRef.current.find(x => x.FieldReferenceId === e.SourceFieldReferenceId) ?? null;
            result.push(new PathAIMappingDto(e.SourceFieldReferenceId, e.TransformationType ?? 0, e.TransformationParameters ?? "", e.DisplayName, e.Description, existing?.IsSelected ?? false, e.Filters, e.DeclaredVariables));
        });

        changes.forEach((e: DataChange) => {

            let row = result.find((x) => x.FieldReferenceId === e.key) ?? null;

            if (row !== null) {

                if (e.data.SourceFieldReferenceId !== undefined) {
                    row.FieldReferenceId = e.data.SourceFieldReferenceId;
                }
                if (e.data.TransformationType !== undefined) {
                    row.TransformationLogicId = e.data.TransformationType ?? 0;
                }
                if (e.data.TransformationParameters !== undefined) {
                    row.TransformationParameters = e.data.TransformationParameters;
                }
                if (e.data.DisplayName !== undefined) {
                    row.DisplayName = e.data.DisplayName;
                }
                if (e.data.Description !== undefined) {
                    row.Description = e.data.Description;
                }
                if (e.data.Filters !== undefined) {
                    row.Filters = e.data.Filters;
                }
                if (e.data.DeclaredVariables !== undefined) {
                    row.DeclaredVariables = e.data.DeclaredVariables;
                }
            }
        });

        pathAIMappingDtosRef.current = result;

    }, []);

    const handleSelectionChanges = useCallback((ids: number[]) => {
        selectedIdsRef.current = ids;
        markChanged();
    }, []);

    const handleAdvancedFilterModeChange = useCallback((value: boolean) => {
        if (advancedFilterModeLocked && !value) {
            return;
        }
        setAdvancedFilterMode(value);
        markChanged();
    }, [advancedFilterModeLocked]);

    const handleDataSourceRefreshed = useCallback(() => {
        dataLoad();
    }, [dataLoad]);

    const handleTableMappingChange = useCallback((tableMappingReadDto: DataTableReadDto) => {
        setPathAIDetail((previous) => {
            if (previous === null) {
                return previous;
            }

            markChanged();

            return new GetPathAIDetailResult(
                previous.Id,
                previous.Name,
                tableMappingReadDto,
                previous.Mappings,
                previous.Schedule,
                previous.LastVersionId,
                previous.PublishedVersionId,
                previous.AdvancedFilterMode);
        });

    }, []);

    function GetPathAIMappingCapabilities(fieldId: number) {
        return capabilities.find((e) => e.SourceFieldId === fieldId) ?? null;
    }

    function GetFieldName(fieldId: number) {
        return mapping.find((e) => e.SourceFieldReferenceId === fieldId)?.Name ?? "";
    }

    function buildPathValidationInput(): PathBeforeSaveValidationInput {
        const selectedRows: ValidationRow[] = pathAIMappingDtosRef.current
            .filter((e) => selectedIdsRef.current.includes(e.FieldReferenceId))
            .map((e) => ({
                labelFieldId: e.FieldReferenceId,
                transformationLogicId: e.TransformationLogicId,
                transformationParameters: e.TransformationParameters,
                fieldName: e.DisplayName,
            }));

        return {
            name: pathAIDetail?.Name,
            source: pathAIDetail?.Source,
            selectedRows,
            fieldNameMode: 'free',
            transformationsCapabilities,
            getAllowedTransformationLogicIds: (row) =>
                GetPathAIMappingCapabilities(row.labelFieldId)?.TransformationLogicIds ?? null,
            getFieldLabel: (fieldId) => GetFieldName(fieldId),
        };
    }

    function CheckBeforeSaveAndPublish(): PathCheckResultDto {
        return checkPathBeforeSaveAndPublish(buildPathValidationInput());
    }

    async function save(publish: boolean): Promise<boolean> {
        const detail = pathAIDetail;
        if (detail === null || detail.Source === null || messageFormHandleRef.current === null) {
            messageFormHandleRef.current?.Open(formatMessage("PathValidation_TechnicalError"), ErrorMessage);
            return false;
        }
        const source = detail.Source;

        const allMappings = pathAIMappingDtosRef.current.map(e => new PathAIMappingDto(e.FieldReferenceId, e.TransformationLogicId, e.TransformationParameters, e.DisplayName, e.Description, selectedIdsRef.current.includes(e.FieldReferenceId), e.Filters, e.DeclaredVariables));

        return performPathSave({
            messageForm: messageFormHandleRef.current,
            checkResult: CheckBeforeSaveAndPublish(),
            checklist: getPathPublishChecklist(buildPathValidationInput()),
            publish,
            persist: () => PathAIApi.UpdatePathAIDetailAsync(pathId, detail.Name, allMappings, source, detail.Schedule, advancedFilterMode, publish),
            onSuccess: (isPublished) => {
                metaApiExposedFieldsRef.current?.Save();
                if (advancedFilterMode) {
                    setAdvancedFilterModeLocked(true);
                }
                changeInProgress.current = false;
                setHasUnsavedChanges(false);
                setIsPublished(isPublished);
                setToastConfig({
                    ...toastConfig,
                    isVisible: true,
                    type: 'success',
                    message: publish ? 'Path saved and published' : 'Path saved as draft',
                    withRedirect: false
                });
            },
        });
    }

    const onHiding = useCallback(() => {
        setToastConfig({
            ...toastConfig,
            isVisible: false,
        });

        if (toastConfig.withRedirect) {
            navigate("/paths");
        }

    }, [toastConfig, navigate]);

    function cancel(): void {

        if (changeInProgress.current) {
            setIsOpen(true);
        }
        else {
            navigate("/paths");
        }
    }

    function cancelFromModal(): void {

        setIsOpen(false);
        navigate("/paths");
    }

    async function saveDraftFromModal(): Promise<void> {

        setIsOpen(false);
        if (await save(false)) {
            navigate("/paths");
        }
    }


    function changePage(index: number): void {
        // Onglet "Filters" indisponible (feature off ou mode avance desactive) : on l'enjambe dans le sens de navigation.
        const target = !filtersTabAvailable && index === FILTERS_TAB
            ? (index > currentTab ? index + 1 : index - 1)
            : index;
        setCurrentTab(target);
        setSearchParams({ tab: target.toString() }, { replace: true });
    };

    function back() {
        changePage(currentTab - 1);
    }

    function next() {
        changePage(currentTab + 1);
    }

    if (pathAIDetail === null) {
        return (<div>Waiting</div>);
    }

    function GetFieldsResume(): FieldResumeDto[] {
        let fieldsResume = new Array<FieldResumeDto>();
        pathAIMappingDtosRef.current.forEach((e) => {
            fieldsResume.push(new FieldResumeDto(
                e.FieldReferenceId,
                GetFieldName(e.FieldReferenceId),
                e.DisplayName));
        });
        return fieldsResume;
    }

    // Single "simple vs complex" filter state for the whole page: simple => the per-row FILTER column
    // is the editor and the Filters tab is hidden; complex => the Filters tab is the editor and the
    // grid column is hidden.
    const enterpriseMode = sessionDetail?.EnterpriseMode ?? false;

    return (
        <React.Fragment>
            <div style={{ width: '100%', height: '100%', padding: 24, flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-start', display: 'inline-flex' }}>
                <div style={{ alignSelf: 'stretch', height: '100%', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 24, display: 'flex' }}>
                    <div style={{ alignSelf: 'stretch', justifyContent: 'space-between', alignItems: 'center', display: 'inline-flex', minHeight: '49px' }}>
                        {editNameInprogress === null ?
                            <div style={{ justifyContent: 'flex-start', alignItems: 'center', gap: 20, display: 'flex' }}>
                                <div style={{ color: '#E3E3E8', fontSize: 24, fontFamily: 'Manrope', fontWeight: '600', lineHeight: '36px', letterSpacing: 0.48, wordWrap: 'break-word' }}>
                                    {pathAIDetail?.Name ?? "???"}
                                </div>
                                <img src="svgs/edit.svg" alt="circle" style={{ cursor: 'pointer' }} onClick={() => setEditNameInprogress(pathAIDetail?.Name ?? "???")} />
                            </div> :
                            <div style={{ width: '50%', justifyContent: 'flex-start', alignItems: 'center', gap: 10, display: 'flex' }}>
                                <TextBox style={{ width: '50%' }} defaultValue={pathAIDetail?.Name ?? "???"} onValueChange={(e) => setEditNameInprogress(e)} />
                                <Button icon="check" onClick={() => handleNameChange()} />
                                <Button icon="close" onClick={() => setEditNameInprogress(null)} />
                            </div>}
                        <Button text="Close" type="normal" stylingMode="text" onClick={() => cancel()} />
                    </div>
                    <Tabs width='100%' stylingMode='secondary' onSelectedIndexChange={changePage} selectedIndex={currentTab} >
                        <Item text="Source">
                        </Item>
                        <Item text="Exposed field(s)">
                        </Item>
                        <Item text="Filters" visible={filtersTabAvailable} disabled={pathAIDetail.Source instanceof PathDataTableReadDto}>
                        </Item>
                        <Item text="Checking">
                        </Item>
                    </Tabs>
                    {(currentTab === 0) ? <SourceSelection OriginTableId={null} OriginPathId={null} Direction="Source" WithDataTable={true} TableMapping={pathAIDetail.Source ?? null} SetTableMapping={handleTableMappingChange} DataSourceRefreshed={handleDataSourceRefreshed} /> : ""}
                    {(currentTab === 1) ? <AIExposedFields
                        ref={metaApiExposedFieldsRef}
                        MappingChanges={dataChangesRef.current}
                        SetMappingChanges={handleDataChanges}
                        Capabilities={capabilities}
                        TransformationsCapabilities={transformationsCapabilities}
                        Mapping={mapping}
                        IsSimpleFilterMode={enterpriseMode}
                        FiltersEnabled={filterTabsEnabled && sourceHandlesFilters}
                        TableReferenceId={(pathAIDetail.Source as DataSourceDataTableReadDto)?.TableReferenceId ?? 0}
                        AdvancedFilterMode={advancedFilterMode}
                        SetAdvancedFilterMode={handleAdvancedFilterModeChange}
                        AdvancedFilterModeLocked={advancedFilterModeLocked}
                        SelectionIds={selectedIdsRef.current}
                        SetSelectionIds={handleSelectionChanges} /> : ""}
                    {(currentTab === 2 && filtersTabAvailable) ? <FiltersSelection TableMapping={pathAIDetail.Source ?? null} SetTableMapping={handleTableMappingChange} WithSimpleParameters={false} DeclaredParameters={collectDeclaredParameters(pathAIMappingDtosRef.current)} SelectedFieldReferenceIds={selectedIdsRef.current} /> : ""}
                    {(currentTab === 3) ? <AIResume Source={pathAIDetail.Source ?? null} Mapping={GetFieldsResume()} SelectionIds={selectedIdsRef.current ?? []} /> : ""}
                </div>
                <div style={{ width: '100%', height: 64, position: 'absolute', bottom: 32, left: 0, display: 'flex', justifyContent: 'space-between', padding: '16px 24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Button icon='back' text="Back to previous step" type="normal" stylingMode="text" onClick={() => back()} disabled={currentTab === 0} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                        <PathVersionStatus HasUnsavedChanges={hasUnsavedChanges} IsPublished={isPublished} />
                        <Button text="Save as draft" type="normal" stylingMode="text" onClick={() => save(false)} disabled={!hasUnsavedChanges} />
                        {currentTab === MAX_TAB ?
                            <Button text="Save and publish" type="default" stylingMode="contained" onClick={() => save(true)} disabled={isPublished && !hasUnsavedChanges} /> :
                            <Button text="Continue to next step" type="default" stylingMode="contained" onClick={() => next()} />}
                    </div>
                </div>
            </div>
            <ChangeInProgressForm IsOpen={isOpen} SetIsOpen={setIsOpen} Ok={() => saveDraftFromModal()} Cancel={() => cancelFromModal()} />
            <MessageForm ref={messageFormHandleRef} />
            <Toast
                visible={toastConfig.isVisible}
                message={toastConfig.message}
                type={toastConfig.type}
                onHiding={onHiding}
                displayTime={1000}
            />
        </React.Fragment>
    )
}
